import Image from "next/image";
import Link from "next/link";
import React from "react";

export interface TreatmentItem {
  title?: string;
  description?: string;
  link?: string;
  imageUrl?: string;
  imageAlt?: string;
  showDisclaimer?: boolean;
}

export interface TreatmentsOverviewProps {
  heading?: string;
  treatments?: TreatmentItem[];
  globalDisclaimerText?: string;
}

/**
 * Treatments Overview Section
 * Shows one realistic visual per treatment (Eugenix-style discipline)
 */
export default function TreatmentsOverview({
  heading = "Hair Restoration Options at Trichos",
  treatments,
  globalDisclaimerText = "",
}: TreatmentsOverviewProps) {
  const items =
    treatments && treatments.length > 0
      ? treatments
      : [
          {
            title: "Scalp Hair Transplant",
            description:
              "Doctor-planned follicular transplantation based on donor quality, scalp condition, and long-term expectations.",
            link: "/blog/scalp-hair-transplant",
          },
          {
            title: "FUE & Long Hair Transplant",
            description:
              "Advanced follicular extraction techniques planned to minimise disruption and support natural appearance.",
            link: "/campaigns/longhairtransplant",
          },
          {
            title: "Facial Hair Transplant",
            description:
              "Precise graft placement for beard, moustache, and eyebrow restoration with attention to symmetry and density.",
            link: "/blog/facial-hair-transplant",
          },
          {
            title: "Repair & Special Cases",
            description:
              "Careful correction of failed or unnatural outcomes using conservative, medically guided approaches.",
            link: "/blog/repair",
          },
        ];

  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <h2 className="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
          {heading}
        </h2>

        <div className="grid gap-4 md:gap-6 grid-cols-2 md:grid-cols-4">
          {items.map((treatment, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white shadow-sm h-[300px] flex flex-col overflow-hidden"
            >
              {/* Treatment Image */}
              <div className="relative h-24 flex-shrink-0 overflow-hidden bg-gray-100">
                {treatment.imageUrl ? (
                  <Image
                    src={treatment.imageUrl}
                    alt={
                      treatment.imageAlt || treatment.title || "Treatment image"
                    }
                    fill
                    className="object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-violet-50 to-violet-100">
                    <div className="text-center">
                      <div className="mb-2 text-4xl">📸</div>
                      <p className="text-sm text-gray-600">
                        {treatment.title || "Treatment Image"}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        (Replace with realistic before/after)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Treatment Info */}
              <div className="p-3 flex-1 flex flex-col justify-between overflow-hidden">
                <div>
                  <h3 className="mb-1 text-base font-bold text-gray-900 line-clamp-1">
                    {treatment.title}
                  </h3>
                  <p className="mb-2 text-xs text-gray-600 line-clamp-2">
                    {treatment.description}
                  </p>
                </div>
                {treatment.link && (
                  <Link
                    href={treatment.link}
                    className="inline-block rounded-full bg-black px-3 py-1 text-xs font-medium transition-colors hover:bg-gray-800 self-start"
                    style={{ color: "#F7E9AE" }}
                  >
                    Learn More
                  </Link>
                )}
                {treatment.showDisclaimer && globalDisclaimerText && (
                  <p className="mt-1 text-xs text-gray-500 line-clamp-1">
                    {globalDisclaimerText}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
